import { useEffect, useMemo, useRef, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridSelectionModel,
  type GridValueFormatterParams,
} from "@mui/x-data-grid-pro";
import { Link as RouterLink, useLocation } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import { format, parseISO } from "date-fns";
import { AcquireBookDataGrid } from "@/components/DataGrid";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { AcquireBookModal } from "@/features/acquireBookStatus/components/AcquireBookModal";
import { getPrefectureIdByValue } from "@/utils/utilsAcquireBook";
import { type ISingleBookAcquireDialogData } from "@/types/singleBookAcquireDialog";
import { useAcquiredProperties } from "../hooks/useAcquiredProperties";
import { writeSearchState } from "../utils/searchState";
import {
  formatDocumentDateForExport,
  formatDocumentStatusForExport,
} from "../utils/documentPresentation";
import { type AcquiredProperty, type AcquiredPropertySearch } from "../types";
import { DocumentCell } from "./DocumentCell";
import { MobilePropertyCards } from "./MobilePropertyCards";
import {
  DOCUMENT_COLUMNS,
  REAL_ESTATE_LABELS,
  canReacquire,
  getCompanyNames,
} from "../utils/propertyPresentation";
import { PropertyExportToolbar } from "./PropertyExportToolbar";

const StyledDataGrid = styled(AcquireBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

interface Props {
  search: AcquiredPropertySearch;
  userId: string;
  isSharable: boolean;
  revalidateIfStale?: boolean;
  onSearchChange: (next: AcquiredPropertySearch, replace?: boolean) => void;
}

export const FeedGrid: React.FC<Props> = ({
  search,
  userId,
  isSharable,
  revalidateIfStale,
  onSearchChange: updateSearch,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const canonical = writeSearchState(search).toString();
  const { data, error, isLoading, isValidating } = useAcquiredProperties(
    search,
    userId,
    isSharable,
    true,
    revalidateIfStale
  );
  const rows = data?.list ?? [];
  const count = data?.count ?? 0;
  useEffect(() => {
    if (
      data &&
      search.page > 0 &&
      search.page * search.pageSize >= data.count
    ) {
      updateSearch(
        {
          ...search,
          page: Math.max(0, Math.ceil(data.count / search.pageSize) - 1),
        },
        true
      );
    }
  }, [data, search]);
  const isReadOnly = useReadOnlyUser();
  const [rowSelection, setRowSelection] = useState<{
    key: string;
    ids: GridSelectionModel;
  }>({ key: canonical, ids: [] });
  const selectedRowIds =
    rowSelection.key === canonical
      ? rowSelection.ids.filter((id) => rows.some((row) => row.id === id))
      : [];
  const [reacquisition, setReacquisition] =
    useState<ISingleBookAcquireDialogData | null>(null);
  useEffect(() => {
    setRowSelection({ key: canonical, ids: [] });
    setReacquisition(null);
  }, [canonical]);
  const columnWidths = useRef<Record<string, number>>({});
  const hasDatalensRequest = rows.some((row) => row.isDatalensRequest);
  const reacquire = (row: AcquiredProperty): void => {
    if (isReadOnly || !canReacquire(row)) return;
    setReacquisition({
      prefecturesId: getPrefectureIdByValue(row.prefectureName) ?? 0,
      prefectures: row.prefectureName,
      address: row.location,
      chiban: row.realEstateType === "LAND" ? row.chibanKaokuNumber : "",
      kaokuNumber: row.realEstateType === "LAND" ? "" : row.chibanKaokuNumber,
      realEstateTypeEng: row.realEstateType,
    });
  };
  const columns = useMemo(() => {
    const returnState = {
      acquiredListReturnTo: location.pathname + location.search,
      acquiredListUserId: userId,
    };
    const result: Array<GridColDef<AcquiredProperty>> = [
      {
        field: "identificationName",
        headerName: "識別名",
        width: 280,
        renderCell: ({
          row,
        }: GridRenderCellParams<unknown, AcquiredProperty>) => (
          <Link
            component={RouterLink}
            to={`/acquirebookstatus/${row.pictureRequestId}/`}
            state={returnState}
            aria-label={`依頼No. ${row.pictureRequestId} の詳細`}
          >
            {row.identificationName ?? `依頼No. ${row.pictureRequestId}`}
          </Link>
        ),
      },
      {
        field: "requestedAt",
        headerName: "取得依頼日",
        width: 180,
        valueFormatter: ({ value }: GridValueFormatterParams<string>) =>
          format(parseISO(value), "yyyy/MM/dd HH:mm"),
      },
      {
        field: "reacquisition",
        headerName: "再取得",
        width: 140,
        align: "center" as const,
        headerAlign: "center" as const,
        disableExport: true,
        renderCell: ({
          row,
        }: GridRenderCellParams<unknown, AcquiredProperty>) => (
          <ReadOnlyTooltip isReadOnly={isReadOnly}>
            <span>
              <Button
                variant="text"
                sx={{ whiteSpace: "nowrap" }}
                disabled={isReadOnly || !canReacquire(row)}
                onClick={() => {
                  reacquire(row);
                }}
              >
                再取得に進む
              </Button>
            </span>
          </ReadOnlyTooltip>
        ),
      },
      ...(hasDatalensRequest
        ? [
            {
              field: "companyNames",
              headerName: "会社名",
              width: 260,
              valueGetter: ({
                row,
              }: GridRenderCellParams<unknown, AcquiredProperty>) =>
                getCompanyNames(row),
              renderCell: ({ value }: GridRenderCellParams<string>) => (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {value}
                </Typography>
              ),
            },
          ]
        : []),
      {
        field: "realEstateType",
        headerName: "種別",
        width: 120,
        valueFormatter: ({ value }: GridValueFormatterParams<string>) =>
          REAL_ESTATE_LABELS[value] ?? value,
      },
      { field: "prefectureName", headerName: "都道府県", width: 120 },
      { field: "location", headerName: "所在", width: 260 },
      {
        field: "chibanKaokuNumber",
        headerName: "地番または家屋番号",
        width: 190,
      },
      ...DOCUMENT_COLUMNS.flatMap(
        ([type, title]): Array<GridColDef<AcquiredProperty>> => [
          {
            field: type,
            headerName: title,
            width: 160,
            headerAlign: "center",
            align: "center",
            valueGetter: ({
              row,
            }: GridRenderCellParams<unknown, AcquiredProperty>) =>
              row.documents
                .filter((document) => document.pictureType === type)
                .map(formatDocumentStatusForExport)
                .join("\n"),
            renderCell: ({
              row,
            }: GridRenderCellParams<unknown, AcquiredProperty>) => (
              <DocumentCell
                documents={row.documents.filter(
                  (document) => document.pictureType === type
                )}
                filters={search}
              />
            ),
          },
          {
            field: `${type}Date`,
            headerName: `${title}取得日`,
            width: 180,
            hide: true,
            valueGetter: ({
              row,
            }: GridRenderCellParams<unknown, AcquiredProperty>) =>
              row.documents
                .filter((document) => document.pictureType === type)
                .map(formatDocumentDateForExport)
                .join("\n"),
          },
        ]
      ),
      ...(isSharable
        ? [
            {
              field: "isOtherUserResource",
              headerName: "他ユーザー取得",
              disableExport: true,
              width: 140,
              align: "center" as const,
              headerAlign: "center" as const,
              valueFormatter: () => "",
              renderCell: ({
                row,
              }: GridRenderCellParams<unknown, AcquiredProperty>) =>
                row.isOtherUserResource ? (
                  <CheckIcon color="action" fontSize="small" />
                ) : (
                  ""
                ),
            },
          ]
        : []),
    ].map((column) => ({
      ...column,
      width: columnWidths.current[column.field] ?? column.width,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    }));
    return result;
  }, [
    hasDatalensRequest,
    isReadOnly,
    isSharable,
    location.pathname,
    location.search,
    search,
    userId,
  ]);
  return (
    <>
      {isMobile ? (
        <MobilePropertyCards
          rows={rows}
          search={search}
          count={count}
          loading={isLoading || isValidating}
          hasError={Boolean(error)}
          onSearchChange={updateSearch}
          selectedIds={selectedRowIds}
          onSelect={(ids) => {
            setRowSelection({ key: canonical, ids });
          }}
          isReadOnly={isReadOnly}
          onReacquire={reacquire}
          userId={userId}
          isSharable={isSharable}
        />
      ) : (
        <Box sx={{ width: "100%" }}>
          <StyledDataGrid
            autoHeight
            stickyHeader
            rows={rows}
            columns={columns}
            onColumnWidthChange={({ colDef, width }) => {
              columnWidths.current[colDef.field] = width;
            }}
            loading={isLoading || isValidating}
            rowCount={count}
            paginationMode="server"
            sortingMode="server"
            filterMode="server"
            page={search.page}
            pageSize={search.pageSize}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(page) => {
              if (data && page !== search.page)
                updateSearch({ ...search, page });
            }}
            onPageSizeChange={(pageSize) => {
              updateSearch({ ...search, pageSize, page: 0 });
            }}
            checkboxSelection
            selectionModel={selectedRowIds}
            onSelectionModelChange={(ids) => {
              setRowSelection({ key: canonical, ids });
            }}
            getRowHeight={() => "auto"}
            disableSelectionOnClick
            components={{
              Toolbar: PropertyExportToolbar,
              NoRowsOverlay: () => (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  {error
                    ? "一覧を取得できませんでした"
                    : "条件に一致する物件がありません"}
                </Stack>
              ),
            }}
            componentsProps={{
              toolbar: {
                selectedCount: selectedRowIds.length,
                pageRowCount: rows.length,
              },
            }}
            sx={{
              "& .MuiDataGrid-cell": {
                minHeight: "52px !important",
                alignItems: "center",
              },
              "& .MuiDataGrid-virtualScroller": { minHeight: 100 },
            }}
          />
        </Box>
      )}
      {reacquisition && (
        <AcquireBookModal
          acquireBookOpen
          handleClose={() => {
            setReacquisition(null);
          }}
          row={reacquisition}
        />
      )}
    </>
  );
};
